import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  TextField,
  IconButton,
  MenuItem,
  Button,
  Select,
  FormControl,
  InputLabel,
  Paper,
} from '@mui/material';
import { Delete, Add, Save } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { loadWorkflowDefinitionSteps, updateWorkflowDefinitionSteps } from './api/workflowApi';
import { useAppDispatch } from '../../../app/hooks';
import { unwrapResult } from '@reduxjs/toolkit';
import { APIError } from '../../../app/appTypes';
import { WorkflowAction, WorkflowRole, WorkflowStep, WorkflowStepActionType, WorkflowStepAssigneeType } from './api/workflowTypes';
import { showError, showSuccess } from '../../notifications/notificationsSlice';
import { loadWorkflowRoles } from './api/workflowRolesApi';


interface WorkflowDefinitionEditorProps {
  definitionId: string;
}


export const WorkflowDefinitionEditor = ({ definitionId }: WorkflowDefinitionEditorProps) => {

  const [steps, setSteps] = useState<WorkflowStep[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const dispatch = useAppDispatch()
  const [error, setError] = useState<APIError | null>(null)

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [roles, setRoles] = useState<WorkflowRole[]>([]);

  const addStep = () => {
    const newStep: WorkflowStep = {
      id: uuidv4(),
      name: `Krok ${steps.length + 1}`,
      assignees: [],
      actions: [
        { id: uuidv4(), label: 'APPROVE', actionType: WorkflowStepActionType.APPROVE, rejectGoToStepId: null },
      ],
    };
    setSteps((prev) => [...prev, newStep]);
    setSelectedIndex(steps.length);
  };

  const removeStep = (index: number) => {
    const updated = [...steps];
    updated.splice(index, 1);
    setSteps(updated);
    setSelectedIndex(null);
  };

  const updateStep = (index: number, data: Partial<WorkflowStep>) => {
    const updated = [...steps];
    updated[index] = { ...updated[index], ...data };
    setSteps(updated);
  };

  const updateAction = (
    stepIndex: number,
    actionIndex: number,
    action: WorkflowAction
  ) => {
    const updated = [...steps];
    updated[stepIndex].actions[actionIndex] = action;
    setSteps(updated);
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      const result = await dispatch(updateWorkflowDefinitionSteps({ id: definitionId, steps }));

      if (updateWorkflowDefinitionSteps.fulfilled.match(result)) {

        const steps = result.payload as WorkflowStep[];
        setSteps(steps);
        dispatch(showSuccess("saved"))
      }
    } catch (err) {
      dispatch(showError("error"))

    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true);
      try {
        const [stepsResult, rolesResult] = await Promise.all([
          dispatch(loadWorkflowDefinitionSteps(definitionId)).then(unwrapResult),
          dispatch(loadWorkflowRoles()).then(unwrapResult),
        ]);

        setSteps(stepsResult);
        setRoles(rolesResult);
      } catch (err) {
        dispatch(showError("error"));
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, [definitionId]);

  return (
    <Box display="flex" gap={4} alignItems="flex-start" sx={{ p: 2 }}>
      <Box width="250px">
        <Typography variant="h6" gutterBottom>
          Kroki
        </Typography>
        <Stepper activeStep={selectedIndex ?? -1} orientation="vertical" nonLinear>
          {steps.map((step, index) => (
            <Step key={step.id}>
              <StepLabel
                onClick={() => setSelectedIndex(index)}
                sx={{ cursor: 'pointer' }}
              >
                {step.name}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box mt={2} display="flex" gap={1}>
          <Button startIcon={<Add />} onClick={addStep} variant="contained">
            Dodaj krok
          </Button>
          <Button
            startIcon={<Save />}
            onClick={handleSave}
            variant="outlined"
            color="primary"
          >
            Zapisz
          </Button>
        </Box>
      </Box>

      <Box flex={1}>
        {selectedIndex === null ? (
          <Typography>Wybierz krok po lewej, aby go edytować</Typography>
        ) : (
          <Paper elevation={3} sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Edytuj krok</Typography>
              <IconButton onClick={() => removeStep(selectedIndex)} color="error">
                <Delete />
              </IconButton>
            </Box>

            <TextField
              label="Nazwa kroku"
              value={steps[selectedIndex].name}
              onChange={(e) =>
                updateStep(selectedIndex, { name: e.target.value })
              }
              fullWidth
              sx={{ mt: 2 }}
            />

            <Typography variant="subtitle1" sx={{ mt: 3 }}>
              Przypisania do kroku:
            </Typography>

            {steps[selectedIndex].assignees.map((assignee, index) => (
              <Box key={index} display="flex" gap={2} alignItems="center" mt={1}>
                <FormControl sx={{ width: '120px' }}>
                  <InputLabel>Typ</InputLabel>
                  <Select
                    value={assignee.assigneeType}
                    label="Typ"
                    onChange={(e) => {
                      const updated = [...steps[selectedIndex].assignees];
                      updated[index] = {
                        assigneeType: e.target.value as WorkflowStepAssigneeType,
                        assigneeValue: '',
                        id: uuidv4(),
                      };
                      updateStep(selectedIndex, { assignees: updated });
                    }}
                  >
                    <MenuItem value="ROLE">Rola</MenuItem>
                  </Select>
                </FormControl>

                <FormControl sx={{ flex: 1 }}>
                  <InputLabel>Rola</InputLabel>
                  <Select
                    value={assignee.assigneeValue}
                    label="Rola"
                    onChange={(e) => {
                      const updated = [...steps[selectedIndex].assignees];
                      updated[index].assigneeValue = e.target.value;
                      updateStep(selectedIndex, { assignees: updated });
                    }}
                  >
                    {roles.map(role => (
                      <MenuItem key={role.code} value={role.code}>
                        {role.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>


                <IconButton
                  onClick={() => {
                    const updated = [...steps[selectedIndex].assignees];
                    updated.splice(index, 1);
                    updateStep(selectedIndex, { assignees: updated });
                  }}
                >
                  <Delete />
                </IconButton>
              </Box>
            ))}
            <Button
              startIcon={<Add />}
              sx={{ mt: 2 }}
              onClick={() => {
                const updated = [...steps[selectedIndex].assignees];
                updated.push({
                  assigneeType: WorkflowStepAssigneeType.ROLE,
                  assigneeValue: '',
                  id: uuidv4()
                });
                updateStep(selectedIndex, { assignees: updated });
              }}
            >
              Dodaj rolę
            </Button>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Typ akcji</InputLabel>
              <Select
                value={steps[selectedIndex].actions[0]?.actionType || ''}
                label="Typ akcji"
                onChange={(e) => {
                  const actionType = e.target.value as WorkflowStepActionType;
                  const newActions: WorkflowAction[] = [];

                  if (actionType === WorkflowStepActionType.APPROVE) {
                    newActions.push(
                      { id: uuidv4(), actionType: WorkflowStepActionType.APPROVE, label: '', rejectGoToStepId: null },
                    );
                  } else {
                    newActions.push(
                      { id: uuidv4(), actionType: WorkflowStepActionType.FINISH, label: '', rejectGoToStepId: null }
                    );
                  }

                  updateStep(selectedIndex, { actions: newActions });
                }}
              >
                <MenuItem value={WorkflowStepActionType.APPROVE}>Approve / Reject</MenuItem>
                <MenuItem value={WorkflowStepActionType.FINISH}>Finish</MenuItem>
              </Select>
            </FormControl>

            {/* Pokaż tylko rejectGoToStepId jeśli jest akcja typu REJECT */}
            {steps[selectedIndex].actions.some(a => a.actionType === WorkflowStepActionType.APPROVE) && (
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Krok po odrzuceniu</InputLabel>
                <Select
                  value={
                    steps[selectedIndex].actions.find(a => a.actionType === WorkflowStepActionType.APPROVE)?.rejectGoToStepId ?? ''
                  }
                  onChange={(e) => {
                    const updatedActions = [...steps[selectedIndex].actions];
                    const rejectActionIndex = updatedActions.findIndex(a => a.actionType === WorkflowStepActionType.APPROVE);
                    if (rejectActionIndex >= 0) {
                      updatedActions[rejectActionIndex].rejectGoToStepId = e.target.value;
                      updateStep(selectedIndex, { actions: updatedActions });
                    }
                  }}
                >
                  {steps.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Paper>
        )}
      </Box>
    </Box>
  );
} 
